/**
 * LLM Workflow Editor Page (DRR-056)
 *
 * Natural language prompt input for editing workflow definitions.
 * Split view: definition preview (left) + suggestion cards (right)
 * Draft management (list, resume, export)
 *
 * Pattern: Uses existing useLlmWorkflowEditor hook
 */

import { useState, useCallback } from 'react';
import {
  Container,
  Title,
  Text,
  Card,
  Group,
  Stack,
  Badge,
  Alert,
  Button,
  Textarea,
  Loader,
  Paper,
  Code,
  ActionIcon,
  Tooltip,
  ScrollArea,
  Divider,
} from '@mantine/core';
import {
  IconWand,
  IconAlertTriangle,
  IconCheck,
  IconX,
  IconArrowBack,
  IconArrowForward,
  IconDownload,
  IconPlayerPlay,
  IconFileText,
  IconTrash,
} from '@tabler/icons-react';
import { useTenantId } from '@/auth';
import { useAuth } from '@/auth/AuthContext';
import { useLlmWorkflowEditor } from '@ferp/workflow-forms';

export function LlmWorkflowEditorPage() {
  const tenantId = useTenantId();
  const { user } = useAuth();
  const [prompt, setPrompt] = useState('');

  const editor = useLlmWorkflowEditor({
    userId: user?.id ?? 'anonymous',
    context: { tenantId: tenantId ?? '' },
  });

  const {
    state,
    createDraft,
    generateSuggestions,
    applySuggestion,
    selectSuggestion,
    undo,
    redo,
    discardDraft,
    persistDraft,
    exportDefinition,
    canUndo,
    canRedo,
    hasUnsavedChanges,
    draftsList,
    draftsLoading,
    loadDraft,
  } = editor;

  const handleGenerate = useCallback(() => {
    if (!prompt.trim()) return;
    generateSuggestions(prompt);
    setPrompt('');
  }, [prompt, generateSuggestions]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        handleGenerate();
      }
    },
    [handleGenerate]
  );

  if (!tenantId) {
    return (
      <Container size="xl" py="xl">
        <Alert icon={<IconAlertTriangle size={16} />} color="yellow" title="No Tenant Selected">
          Please select a tenant to use the workflow editor.
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <div>
            <Title order={2}>LLM Workflow Editor</Title>
            <Text size="sm" c="dimmed">
              Edit workflow definitions with natural language prompts (DRR-056)
            </Text>
          </div>
          <Group gap="xs">
            <Tooltip label="Undo">
              <ActionIcon variant="light" disabled={!canUndo} onClick={undo}>
                <IconArrowBack size={16} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Redo">
              <ActionIcon variant="light" disabled={!canRedo} onClick={redo}>
                <IconArrowForward size={16} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Export definition">
              <ActionIcon variant="light" onClick={exportDefinition}>
                <IconDownload size={16} />
              </ActionIcon>
            </Tooltip>
            {state.draft && (
              <>
                <Button
                  variant="light"
                  color="green"
                  size="xs"
                  leftSection={<IconCheck size={14} />}
                  onClick={() => persistDraft()}
                  disabled={!hasUnsavedChanges}
                >
                  Save Draft
                </Button>
                <Button
                  variant="light"
                  color="red"
                  size="xs"
                  leftSection={<IconTrash size={14} />}
                  onClick={discardDraft}
                >
                  Discard
                </Button>
              </>
            )}
            {!state.draft && (
              <Button
                leftSection={<IconFileText size={16} />}
                variant="light"
                onClick={() => createDraft('Untitled Workflow')}
              >
                New Draft
              </Button>
            )}
          </Group>
        </Group>

        {/* Error Display */}
        {state.error && (
          <Alert icon={<IconAlertTriangle size={16} />} color="red" title="Error">
            {state.error}
          </Alert>
        )}

        {/* Prompt Input */}
        <Card withBorder p="md">
          <Stack gap="sm">
            <Text fw={500} size="sm">Natural Language Prompt</Text>
            <Textarea
              placeholder="Describe what you want to change... (Ctrl+Enter to generate)"
              value={prompt}
              onChange={(e) => setPrompt(e.currentTarget.value)}
              onKeyDown={handleKeyDown}
              minRows={3}
              maxRows={6}
              autosize
            />
            <Group justify="flex-end">
              <Button
                leftSection={state.isGenerating ? <Loader size={14} /> : <IconWand size={16} />}
                onClick={handleGenerate}
                disabled={!prompt.trim() || state.isGenerating}
              >
                {state.isGenerating ? 'Generating...' : 'Generate Suggestions'}
              </Button>
            </Group>
          </Stack>
        </Card>

        {/* Split View: Definition Preview + Suggestions */}
        <Group ta="left" gap="md" grow>
          {/* Left: Definition Preview */}
          <Card withBorder p="md" style={{ flex: 2 }}>
            <Stack gap="sm">
              <Group justify="space-between">
                <Text fw={500} size="sm">Current Definition</Text>
                {hasUnsavedChanges && (
                  <Badge color="yellow" variant="light" size="xs">Unsaved Changes</Badge>
                )}
              </Group>
              <ScrollArea h={400}>
                <Code block style={{ fontSize: '12px', whiteSpace: 'pre' }}>
                  {state.currentDefinition || '# No workflow loaded.\n# Create a new draft or load an existing one.'}
                </Code>
              </ScrollArea>
            </Stack>
          </Card>

          {/* Right: Suggestions */}
          <Card withBorder p="md" style={{ flex: 1 }}>
            <Stack gap="sm">
              <Text fw={500} size="sm">Suggestions</Text>
              {state.pendingSuggestions.length === 0 && (
                <Text size="sm" c="dimmed" ta="center" py="xl">
                  Enter a prompt to generate suggestions.
                </Text>
              )}
              {state.pendingSuggestions.map((suggestion) => (
                <Paper
                  key={suggestion.suggestionId}
                  withBorder
                  p="sm"
                  style={{
                    cursor: 'pointer',
                    borderColor:
                      state.selectedSuggestion?.suggestionId === suggestion.suggestionId
                        ? 'var(--mantine-color-blue-6)'
                        : undefined,
                  }}
                  onClick={() => selectSuggestion(suggestion)}
                >
                  <Stack gap="xs">
                    <Group justify="space-between">
                      <Badge variant="light" size="xs">{suggestion.operation}</Badge>
                      <Badge
                        color={suggestion.confidence > 0.8 ? 'green' : 'yellow'}
                        variant="light"
                        size="xs"
                      >
                        {Math.round(suggestion.confidence * 100)}%
                      </Badge>
                    </Group>
                    <Text size="xs">{suggestion.explanation}</Text>
                    <Group gap="xs">
                      <Button
                        size="xs"
                        variant="light"
                        color="green"
                        leftSection={<IconCheck size={12} />}
                        onClick={(e) => {
                          e.stopPropagation();
                          applySuggestion(suggestion.suggestionId);
                        }}
                        loading={state.isApplying}
                      >
                        Apply
                      </Button>
                      <Button
                        size="xs"
                        variant="light"
                        color="red"
                        leftSection={<IconX size={12} />}
                        onClick={(e) => e.stopPropagation()}
                      >
                        Reject
                      </Button>
                    </Group>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          </Card>
        </Group>

        {/* Drafts List */}
        <Divider />
        <Stack gap="sm">
          <Text fw={500} size="sm">Saved Drafts</Text>
          {draftsLoading && <Loader size="sm" />}
          {!draftsLoading && (!draftsList || draftsList.length === 0) && (
            <Text size="sm" c="dimmed">No saved drafts.</Text>
          )}
          {draftsList?.map((draft) => (
            <Paper key={draft.draftId} withBorder p="sm">
              <Group justify="space-between">
                <Stack gap={2}>
                  <Text size="sm" fw={500}>{draft.workflowName || 'Untitled Draft'}</Text>
                  <Text size="xs" c="dimmed">
                    {draft.updatedAt
                      ? new Date(draft.updatedAt).toLocaleString()
                      : 'Unknown date'}
                  </Text>
                </Stack>
                <Button
                  size="xs"
                  variant="light"
                  leftSection={<IconPlayerPlay size={14} />}
                  onClick={() => loadDraft(draft.draftId)}
                >
                  Resume
                </Button>
              </Group>
            </Paper>
          ))}
        </Stack>
      </Stack>
    </Container>
  );
}

export default LlmWorkflowEditorPage;
